/**
 * Advanced Commerce (GrapheneHC) adapter.
 *
 * GrapheneHC renders the listing on its own servers and injects the HTML into a
 * mount element. This module is the seam between that and the theme: it waits
 * for the library, asks for the configured template, and then hands the injected
 * markup back to the theme's own behaviour.
 *
 * The theme's interactive parts are custom elements (product-card, grid-load-more,
 * quantity inputs and so on), so anything GrapheneHC injects that uses those tags
 * upgrades itself the moment it lands in the DOM — no re-initialisation needed.
 * That is the main reason the console template should emit *our* card markup
 * rather than a lookalike: matching the tags buys the behaviour for free.
 *
 * See docs/advanced-commerce.md for the markup contract.
 */

/** How long to wait for the third-party library before giving up, in ms. */
const LIBRARY_TIMEOUT = 8000;

/** Poll interval while waiting for the library, in ms. */
const LIBRARY_POLL_INTERVAL = 50;

/** Set once graphenehc.init() has run, so it is only ever called once per page. */
let libraryConfigured = false;

/**
 * Applies page-wide GrapheneHC settings before the first request.
 *
 * Currently just the console theme: GrapheneHC renders with the console's
 * primary theme unless init() is given a theme GUID, and it then sends that GUID
 * with every load, search and autocomplete request. The GUID comes from the
 * "Console Theme ID" theme setting (snippets/graphene-head.liquid) and is left
 * out entirely when blank, so the default stays the primary theme.
 *
 * @param {object} graphene the GrapheneHC library
 * @returns {object} the same library, for chaining
 */
function configureLibrary(graphene) {
  if (libraryConfigured) return graphene;
  libraryConfigured = true;

  translatePageParam(graphene);

  const tag = document.querySelector("[data-graphene-global]");
  if (!tag) return graphene;

  try {
    const { theme } = JSON.parse(tag.textContent);
    if (theme && typeof graphene.init === "function") {
      graphene.init({ theme });
    }
  } catch (error) {
    console.error("[graphene] could not apply global settings", error);
  }

  return graphene;
}

const PAGE_PARAM = "page";

const GRAPHENE_PAGE_PARAM = "pg";

function translatePageParam(graphene) {
  if (typeof graphene.ajax !== "function") return;

  const ajax = graphene.ajax;

  graphene.ajax = function (options) {
    const original = window.location.href;
    const url = new URL(original);
    const page = url.searchParams.get(PAGE_PARAM);

    if (!page) return ajax.call(this, options);

    url.searchParams.delete(PAGE_PARAM);
    url.searchParams.set(GRAPHENE_PAGE_PARAM, page);

    if (options?.body?.x_page_url) options.body.x_page_url = url.href;

    history.replaceState(history.state, "", url);
    try {
      return ajax.call(this, options);
    } finally {
      history.replaceState(history.state, "", original);
    }
  };
}

/**
 * Resolves once `window.graphenehc` exists, configured for this page.
 *
 * The library is loaded with `defer` (see snippets/graphene-head.liquid), so it
 * is normally present before this module runs. Polling rather than assuming
 * covers the case where it is slow, blocked by an ad blocker, or the URL setting
 * is wrong — in which case we reject and the caller leaves the page alone.
 *
 * Every mount and the autocomplete get the library through here, which is what
 * guarantees configureLibrary() runs before any of their requests.
 *
 * @returns {Promise<object>} the GrapheneHC library
 */
function whenLibraryReady() {
  return new Promise((resolve, reject) => {
    if (window.graphenehc) {
      resolve(configureLibrary(window.graphenehc));
      return;
    }

    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (window.graphenehc) {
        clearInterval(timer);
        resolve(configureLibrary(window.graphenehc));
      } else if (Date.now() - startedAt > LIBRARY_TIMEOUT) {
        clearInterval(timer);
        reject(new Error("GrapheneHC library did not load"));
      }
    }, LIBRARY_POLL_INTERVAL);
  });
}

/**
 * Reads the config the Liquid mount wrote out.
 *
 * @param {Element} mount
 * @returns {object|null}
 */
function readConfig(mount) {
  // The config tag is a sibling of the mount rather than a child, because the
  // mount's contents are replaced wholesale by GrapheneHC.
  const tag = mount.parentElement?.querySelector("[data-graphene-config]");

  if (!tag) return null;

  try {
    return JSON.parse(tag.textContent);
  } catch (error) {
    console.error("[graphene] could not parse mount config", error);
    return null;
  }
}

/**
 * Runs after GrapheneHC has injected markup.
 *
 * Custom elements self-upgrade, so this only has to cover the things that do
 * not: telling the rest of the theme that new cards exist, and clearing the
 * loading state. Anything that needs to react to a new grid should listen for
 * `graphene:rendered` rather than being called from here, so this stays a
 * notification point instead of turning into a list of every module.
 *
 * @param {Element} mount
 * @param {object} config
 */
function onRendered(mount, config, response) {
  mount.classList.add("graphene-results--loaded");
  mount.removeAttribute("data-graphene-loading");

  const listing = response?.templates?.[config.templateId];
  syncPager(mount, listing);
  noindexIfOutOfRange(config, listing);

  // Bubbles so listeners can bind at document level without knowing the mount.
  mount.dispatchEvent(
    new CustomEvent("graphene:rendered", {
      bubbles: true,
      detail: { surface: config.surface, mount },
    }),
  );

  // Opt-in: the live theme always scrolls the results into view, which fights
  // the browser restoring scroll position on a back-navigation.
  if (config.scrollOnLoad) {
    mount.scrollIntoView();
  }
}

/**
 * Marks the mount as failed and leaves the page usable.
 *
 * There is no automatic fall back to the theme's own grid: by the time this runs
 * the server has already committed to the GrapheneHC branch, and the products
 * were never rendered. Recovery is a merchant action — switch the surface off in
 * theme settings — so the honest thing here is to say so in the console and show
 * an empty state rather than silently leaving a spinner running.
 *
 * @param {Element} mount
 * @param {Error} error
 */
function onFailure(mount, error) {
  console.error("[graphene] results failed to load", error);
  mount.removeAttribute("data-graphene-loading");
  mount.setAttribute("data-graphene-error", "");
}

/**
 * Mounts one GrapheneHC results container.
 *
 * @param {Element} mount
 */
async function mountResults(mount) {
  const config = readConfig(mount);

  if (!config) return;

  mount.setAttribute("data-graphene-loading", "");

  let graphene;

  try {
    graphene = await whenLibraryReady();
  } catch (error) {
    onFailure(mount, error);
    return;
  }

  try {
    graphene.load({
      template: { id: config.templateId, target: config.target },
      page_context: config.pageContext,
      args: config.args,
      success: (response) => onRendered(mount, config, response),
      error: (error) => onFailure(mount, error),
    });
  } catch (error) {
    onFailure(mount, error);
  }
}

document.querySelectorAll("[data-graphene-results]").forEach(mountResults);

/**
 * Product page recommendations - GrapheneHC's pdp_recs_1 element, as the live
 * theme loads it. sections/recommendations.liquid renders the slider shell with
 * an empty track; the element template (docs/graphene/pdp_recs_1.scriban)
 * returns slides only, injected into that track. Afterwards the slider is
 * (re)started, or the section hidden when nothing came back - an empty "You may
 * also like" is worse than none.
 *
 * @param {Element} slider - the [data-graphene-recs] blaze slider
 */
async function mountRecommendations(slider) {
  if (slider.dataset.grapheneRecsBound) return;
  slider.dataset.grapheneRecsBound = "true";

  const tag = slider.parentElement?.querySelector("[data-graphene-recs-config]");
  let config;

  try {
    config = JSON.parse(tag?.textContent || "");
  } catch (error) {
    console.error("[graphene] could not parse recommendations config", error);
    return;
  }

  const section = slider.closest(".shopify-section") || slider.closest("section");
  const hideSection = () => {
    if (section) section.hidden = true;
  };

  let graphene;

  try {
    graphene = await whenLibraryReady();
  } catch (error) {
    console.error("[graphene] recommendations unavailable", error);
    hideSection();
    return;
  }

  const onLoaded = async () => {
    if (!slider.querySelector("product-card")) {
      hideSection();
      return;
    }

    // GrapheneHC wraps what it injects in a container of its own, so the track
    // ends up holding one child with every card stacked inside it. Blaze treats
    // each direct child of the track as a slide, so the slides are lifted out
    // and the wrapper dropped.
    const track = slider.querySelector(".blaze-track");
    if (track) {
      const slides = [...track.querySelectorAll("div.product")].filter((el) =>
        el.querySelector("product-card"),
      );
      if (slides.length && slides.some((el) => el.parentElement !== track)) {
        track.replaceChildren(...slides);
      }
    }

    try {
      // The page-load watcher may already have started this slider over an
      // empty track. Blaze counts its slides when it starts, so it is stopped
      // and started again rather than refreshed.
      const { blazeSliderController } = await import("@theme/blaze-slider-controller");
      blazeSliderController.destroy(slider);
      blazeSliderController.init(slider);
    } catch (error) {
      console.warn("[graphene] recommendations slider failed to start", error);
    }

    slider.classList.remove("blaze-loading");
    slider.dispatchEvent(
      new CustomEvent("graphene:rendered", {
        bubbles: true,
        detail: { surface: "recommendations", mount: slider },
      }),
    );
  };

  try {
    graphene.load({
      elements: [{ id: config.elementId, target: config.target }],
      page_context: config.pageContext,
      args: config.args,
      success: onLoaded,
      error: (error) => {
        console.error("[graphene] recommendations failed to load", error);
        hideSection();
      },
    });
  } catch (error) {
    console.error("[graphene] recommendations failed to load", error);
    hideSection();
  }
}

document.querySelectorAll("[data-graphene-recs]").forEach(mountRecommendations);

/**
 * Binds GrapheneHC's typeahead to the header search input.
 *
 * The theme's own predictive overlay is not rendered at all when this runs (see
 * snippets/predictive-search.liquid), so there is no need to suppress it here —
 * the two never coexist.
 *
 * @param {Element} container
 */
async function mountAutocomplete(container) {
  const tag = document.querySelector("[data-graphene-autocomplete-config]");

  if (!tag) return;

  let config;

  try {
    config = JSON.parse(tag.textContent);
  } catch (error) {
    console.error("[graphene] could not parse autocomplete config", error);
    return;
  }

  let graphene;

  try {
    graphene = await whenLibraryReady();
  } catch (error) {
    // A failed typeahead is not worth breaking the header over: the input is a
    // real form field and still submits to the search page without it.
    console.error("[graphene] autocomplete unavailable", error);
    return;
  }

  const overlay = config.overlaySelector ? document.querySelector(config.overlaySelector) : null;

  const setOpen = (open) => {
    if (!overlay) return;

    overlay.classList.toggle("is-open", open);
    overlay.setAttribute("aria-hidden", String(!open));

    if (open) {
      overlay.removeAttribute("inert");
    } else {
      overlay.setAttribute("inert", "");
    }

    document.body.classList.toggle("predictive-search-open", open);
  };

  graphene.autocomplete({
    input_selector: config.inputSelector,
    dropdown_selector: config.dropdownSelector,
    page_context: { currency_id: window.Shopify?.currency?.active },
    args: config.args,
    // Their script otherwise pins the dropdown with inline top/left/width. The
    // theme's panel is a full-width surface below the header, so positioning is
    // handed back to CSS with a no-op.
    position: () => {},
    // show/hide default to setting style.display. Driving the overlay instead
    // keeps the theme's semantics - scrim, aria-hidden and inert move together.
    show: () => setOpen(true),
    hide: () => setOpen(false),
    after_load: () => {
      container.dispatchEvent(
        new CustomEvent("graphene:autocomplete", { bubbles: true, detail: { container } }),
      );
    },
  });

  // GrapheneHC calls stopPropagation() on every click inside the dropdown so its
  // own click-away handler on <body> does not close it. That also hides those
  // clicks from the theme's delegated listeners — wishlist.js (body) and
  // sample-box.js (document) — so the heart and Order Sample did nothing and the
  // click fell through to the card link. A listener on the dropdown itself still
  // runs, so the card controls are handed to their modules from here.
  document.querySelector(config.dropdownSelector)?.addEventListener("click", async (event) => {
    const heart = event.target.closest("[wishlist-button]");

    if (heart) {
      event.preventDefault();
      const { wishlist } = await import("@theme/wishlist");
      wishlist.wishlistHeartClick({ target: heart, preventDefault() {} });
      return;
    }

    const sample = event.target.closest("[data-sample-add]");

    if (sample) {
      event.preventDefault();
      const { addSample } = await import("@theme/sample-box");
      addSample(sample);
    }
  });

  // The header field and the panel's own field are both bound by GrapheneHC, so
  // either one drives the results - but they held their own values, so a term
  // typed in the header left the panel's field empty (and clearing one left the
  // other showing a stale term). Whichever is typed in, the rest follow.
  document.addEventListener("input", (event) => {
    const source = event.target.closest?.(config.inputSelector);
    if (!source) return;

    document.querySelectorAll(config.inputSelector).forEach((input) => {
      if (input !== source && input.value !== source.value) input.value = source.value;
    });
  });

  // Clicking away or pressing Escape closes it, matching the theme's overlay.
  document.addEventListener("click", (event) => {
    if (!overlay || !overlay.classList.contains("is-open")) return;
    if (event.target.closest(config.dropdownSelector)) return;
    if (event.target.closest(config.inputSelector)) return;

    setOpen(false);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") setOpen(false);
  });

  // The reused search field brings the theme's reset button with it. search.js
  // normally wires that; it is not running on this surface, so it closes the
  // panel and clears the term here instead.
  //
  // The header field's own close (mobile, where the panel has no field of its
  // own and this is the only way out) is wired the same way, from the document
  // because it sits outside the overlay.
  const clearAndClose = () => {
    document.querySelectorAll(config.inputSelector).forEach((input) => {
      input.value = "";
    });

    setOpen(false);
  };

  overlay?.addEventListener("click", (event) => {
    if (!event.target.closest("#search-reset")) return;
    clearAndClose();
  });

  document.addEventListener("click", (event) => {
    if (!event.target.closest("[data-header-search-close]")) return;
    event.preventDefault();
    clearAndClose();
  });
}

document.querySelectorAll("[data-graphene-autocomplete]").forEach(mountAutocomplete);

/* -----------------------
  LOAD MORE
----------------------- */

/** Matches the spinner the theme's own load-more button injects. */
const LOADER_MARKUP = `
  <span class="grid-loader" aria-hidden="true">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" focusable="false" aria-hidden="true">
      <path fill="currentColor" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z">
        <animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.6s" repeatCount="indefinite"/>
      </path>
    </svg>
  </span>
`;

const pagers = new WeakMap();

function pageUrl(page) {
  const url = new URL(window.location.href);
  url.searchParams.delete(GRAPHENE_PAGE_PARAM);

  if (page > 1) {
    url.searchParams.set(PAGE_PARAM, page);
  } else {
    url.searchParams.delete(PAGE_PARAM);
  }

  return url;
}

function requestedPage() {
  const params = new URLSearchParams(window.location.search);
  return parseInt(params.get(PAGE_PARAM) || params.get(GRAPHENE_PAGE_PARAM), 10) || 1;
}

function syncPager(mount, listing) {
  if (!pagers.has(mount)) {
    const pager = mount.parentElement?.querySelector("[data-graphene-pager]");
    if (!pager) return;
    pagers.set(mount, pager);
  }

  const pager = pagers.get(mount);
  const link = pager.querySelector("[data-graphene-load-more]");

  mount.querySelectorAll(".graphene-load-more").forEach((element) => {
    if (element !== pager) element.remove();
  });

  const current = listing?.current_page;
  const grid = mount.querySelector(link.dataset.target || "#product-grid");

  if (!current || current >= listing.page_count || !grid) {
    pager.remove();
    return;
  }

  link.href = pageUrl(current + 1).href;
  link.dataset.nextPage = current + 1;
  grid.after(pager);
  pager.setAttribute("data-ready", "");
}

function noindexIfOutOfRange(config, listing) {
  if (config.surface !== "collection" || !listing) return;
  if (requestedPage() <= Math.max(listing.page_count || 0, 1)) return;

  let robots = document.head.querySelector('meta[name="robots"]');

  if (!robots) {
    robots = document.createElement("meta");
    robots.name = "robots";
    document.head.append(robots);
  }

  robots.content = "noindex";
}

/**
 * Appends the next page of GrapheneHC results.
 *
 * The theme's grid-load-more element cannot be reused - it fetches Shopify
 * section URLs. GrapheneHC does the equivalent itself: load_page() sets
 * is_paging_request so `plp` returns only the product list, then appends it into
 * the target. All this has to do is call it and manage the button.
 *
 * load_page() carries the first load's template and page_context over, but not
 * its args — so the mount's args are re-sent here. Without them the paging
 * request falls back to vat_multiplier 1 and the appended cards show ex-VAT.
 *
 * @param {HTMLButtonElement} button
 */
function loadNextPage(button) {
  if (button.hasAttribute("aria-busy")) return;

  const graphene = window.graphenehc;

  if (!graphene || typeof graphene.load_page !== "function") {
    console.error("[graphene] load_page unavailable");
    return;
  }

  const mount = button.closest("[data-graphene-results]");
  const config = mount ? readConfig(mount) : null;
  const label = button.textContent;
  const nextPage = Number(button.dataset.nextPage);

  button.setAttribute("aria-busy", "true");
  // Locked so swapping the label for a spinner does not collapse the button.
  button.style.minWidth = `${button.offsetWidth}px`;
  button.innerHTML = LOADER_MARKUP;

  const restore = () => {
    button.removeAttribute("aria-busy");
    button.style.minWidth = "";
    button.textContent = label;
  };

  const paging = nextPage
    ? { page_number: nextPage, set_page_url: false }
    : { track_page: true, set_page_url: true };

  try {
    if (nextPage) history.pushState(history.state, "", pageUrl(nextPage));

    graphene.load_page({
      type: "next",
      ...paging,
      ...(config?.args && { args: config.args }),
      target: {
        selector: button.dataset.target || "#product-grid",
        position: "beforeend",
      },
      success: (response) => {
        restore();
        if (mount && config) syncPager(mount, response?.templates?.[config.templateId]);
        button.dispatchEvent(new CustomEvent("graphene:page-loaded", { bubbles: true }));
      },
      // Fired once the page just appended is the last one, so the control goes
      // rather than sitting there doing nothing.
      last_page: () => {
        button.closest(".graphene-load-more")?.remove();
      },
      error: (error) => {
        console.error("[graphene] load_page failed", error);
        restore();
      },
    });
  } catch (error) {
    console.error("[graphene] load_page failed", error);
    restore();
  }
}

// Delegated: the button is replaced every time the listing re-renders.
document.addEventListener(
  "click",
  (event) => {
    const button = event.target.closest("[data-graphene-load-more]");

    if (!button) return;

    event.stopPropagation();

    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;

    event.preventDefault();
    loadNextPage(button);
  },
  true,
);
